import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../../components-v2/Button';
import { Input } from '../../../../../../../components-v2/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../../../components-v2/Popover';
import { ButtonBase } from '../../../../../../../components/Button';
import { useTagsQuery } from '../../../../../../../../queries/Tag/useTagsQuery';
import { styleSheet } from './styles';

interface IProps {
	appliedTagNames: string[];
	onSelect: (name: string) => void;
	trigger: React.ReactNode;
	impersonationContext?: Api.IImpersonationContext;
}

export function AddTagPopover({ appliedTagNames, onSelect, trigger, impersonationContext }: IProps): JSX.Element {
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState('');

	const tagsQuery = useTagsQuery({
		enabled: open,
		pageSize: 25,
		query: search || undefined,
		sortDescriptor: { sortBy: 'Stats.Contacts', sort: 'desc' },
		impersonationContext,
	});

	const options = (tagsQuery.data?.pages.flatMap(page => page.values) ?? []).filter(
		tag => !appliedTagNames.includes(tag.name)
	);

	const handleOpenChange = (next: boolean) => {
		setOpen(next);
		if (!next) {
			setSearch('');
		}
	};

	const handleSelect = (name: string) => {
		onSelect(name);
		setOpen(false);
		setSearch('');
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>{trigger}</PopoverTrigger>
			<PopoverContent align='start' styles={[styleSheet.addTagPopoverContent]}>
				<div className={css(styleSheet.addTagPopoverInputWrap)}>
					<Input placeholder='Search tags…' value={search} onChange={ev => setSearch(ev.target.value)} />
				</div>
				{tagsQuery.isError ? (
					<div className={css(styleSheet.addTagPopoverError)}>
						<span>{"Couldn't load tags"}</span>
						<Button variant='ghost' onClick={() => tagsQuery.refetch()}>
							Retry
						</Button>
					</div>
				) : (
					<>
						<div className={css(styleSheet.addTagPopoverSectionLabel)}>MOST USED</div>
						<div role='listbox' aria-label='Account tags' className={css(styleSheet.addTagPopoverList)}>
							{options.map(tag => (
								<ButtonBase
									key={tag.id}
									role='option'
									aria-selected={false}
									className={css(styleSheet.addTagPopoverOption)}
									onClick={() => handleSelect(tag.name)}
								>
									{tag.name}
								</ButtonBase>
							))}
						</div>
					</>
				)}
			</PopoverContent>
		</Popover>
	);
}
